import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Tag, X } from 'lucide-react';
import { SaleOffer } from '../../api/SalesAPI';
import TextOptionalActionButton from '../TextOptionalActionButton/TextOptionalActionButton';
import SaleOfferFields from '../SaleOfferFields/SaleOfferFields';
import Tooltip from '../Tooltip/Tooltip';
import { formatOfferSummary, isOfferPresent } from '../../utils/saleLineItems';

type Props = {
  offer?: SaleOffer;
  baseAmount: number;
  currency?: string;
  onChange: (offer: SaleOffer | undefined) => void;
  isDisabled?: boolean;
  disabledHint?: string;
  addDiscountLabel?: string;
  disabledActionLabel?: string;
  onDisabledAction?: () => void;
  variant?: 'inline' | 'rowAction';
  /** rowAction: render only the add/edit trigger (place in price row) */
  actionOnly?: boolean;
  /** rowAction: render only the expanded form (place below the row) */
  formOnly?: boolean;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
};

function SaleOfferControl({
  offer,
  baseAmount,
  currency = 'EUR',
  onChange,
  isDisabled = false,
  disabledHint,
  addDiscountLabel,
  disabledActionLabel,
  onDisabledAction,
  variant = 'inline',
  actionOnly = false,
  formOnly = false,
  isExpanded: controlledExpanded,
  onExpandedChange,
}: Props) {
  const { t } = useTranslation();
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;

  const setExpanded = (value: boolean) => {
    if (!isControlled) setInternalExpanded(value);
    onExpandedChange?.(value);
  };

  const hasOffer = isOfferPresent(offer);
  const addLabel = addDiscountLabel ?? (t('sales.form.offer.add_discount') || 'Add discount');

  const handleChange = (next: SaleOffer | undefined) => {
    onChange(next);
    if (!isOfferPresent(next)) {
      setExpanded(false);
    }
  };

  const handleRemove = () => {
    onChange(undefined);
    setExpanded(false);
  };

  const renderRowActionPill = ({
    label,
    onClick,
    disabled = false,
  }: {
    label: string;
    onClick?: () => void;
    disabled?: boolean;
  }) => {
    const pillClasses = disabled
      ? 'flex items-center space-x-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-400 opacity-50'
      : 'flex items-center space-x-1 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-700 transition-colors hover:bg-blue-200';

    const content = (
      <>
        <Tag className="h-3.5 w-3.5" aria-hidden />
        <span>{label}</span>
      </>
    );

    if (onClick && !disabled) {
      return (
        <button
          type="button"
          onClick={onClick}
          className="shrink-0 border-0 bg-transparent p-0"
        >
          <span className={pillClasses}>{content}</span>
        </button>
      );
    }

    return <div className={`shrink-0 ${pillClasses}`}>{content}</div>;
  };

  const renderRemoveDiscountButton = () => {
    const label = String(t('sales.form.offer.remove_discount') || 'Remove discount');
    return (
      <Tooltip content={label} position="bottom">
        <button
          type="button"
          onClick={handleRemove}
          aria-label={label}
          className="rounded-full p-1 transition-colors hover:bg-red-200 group"
        >
          <X className="h-4 w-4 text-red-600 group-hover:text-red-700" aria-hidden />
        </button>
      </Tooltip>
    );
  };

  const renderRowActionSeeDiscount = () => (
    <div className="flex shrink-0 items-center gap-2">
      {renderRowActionPill({
        label: t('sales.form.offer.see_discount') || 'See discount',
        onClick: () => setExpanded(true),
        disabled: isDisabled,
      })}
      {!isDisabled && renderRemoveDiscountButton()}
    </div>
  );

  const renderForm = () => (
    <div className="space-y-2">
      <SaleOfferFields
        offer={offer}
        baseAmount={baseAmount}
        currency={currency}
        onChange={handleChange}
        onClear={handleRemove}
        isDisabled={isDisabled}
        disabledHint={disabledHint}
        compact={variant === 'rowAction'}
        hideTitle
        hideClearButton
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="shrink-0 border-0 bg-transparent p-0"
        >
          <span className="flex items-center space-x-1 rounded-full bg-green-100 px-2 py-1 text-xs text-green-700 transition-colors hover:bg-green-200">
            <Check className="h-3.5 w-3.5" aria-hidden />
            <span>{t('sales.form.offer.done') || 'Done'}</span>
          </span>
        </button>
      </div>
    </div>
  );

  if (formOnly) {
    if (!expanded) return null;
    return renderForm();
  }

  if (expanded && !isDisabled) {
    if (actionOnly) {
      if (variant === 'rowAction') {
        return renderRowActionPill({
          label: hasOffer
            ? t('sales.form.offer.discount') || 'Discount'
            : addLabel,
        });
      }
      return null;
    }
    return renderForm();
  }

  if (isDisabled && !hasOffer) {
    if (formOnly) return null;
    return (
      <div className={variant === 'rowAction' ? 'shrink-0' : undefined}>
        {variant === 'rowAction' ? (
          renderRowActionPill({
            label: addLabel,
            disabled: true,
          })
        ) : (
          <span className="text-xs text-gray-400 underline cursor-default opacity-50">
            {addLabel}
          </span>
        )}
        {disabledHint && (
          <div className={`${variant === 'rowAction' ? 'sr-only' : 'mt-1 space-y-1'}`}>
            <p className="text-xs text-amber-700">{disabledHint}</p>
            {variant !== 'rowAction' && onDisabledAction && disabledActionLabel && (
              <button
                type="button"
                onClick={onDisabledAction}
                className="text-xs text-blue-600 underline hover:text-blue-700"
              >
                {disabledActionLabel}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (!hasOffer) {
    if (formOnly) return null;
    if (variant === 'rowAction') {
      return renderRowActionPill({
        label: addLabel,
        onClick: () => setExpanded(true),
      });
    }
    return (
      <TextOptionalActionButton
        text={addLabel}
        onClick={() => setExpanded(true)}
        disabled={isDisabled}
        variant="blue"
      />
    );
  }

  if (formOnly) return null;

  const summary = formatOfferSummary(offer!, currency, baseAmount);

  if (variant === 'inline') {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="rounded-md bg-gray-100 px-2 py-1 font-medium text-gray-700">{summary}</span>
        {!isDisabled && (
          <>
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-blue-600 underline hover:text-blue-700"
            >
              {t('sales.form.offer.see_discount') || 'See discount'}
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="text-gray-500 underline hover:text-gray-800"
            >
              {t('sales.form.offer.remove_discount') || 'Remove'}
            </button>
          </>
        )}
      </div>
    );
  }

  return renderRowActionSeeDiscount();
}

export default SaleOfferControl;
